import * as fs from 'node:fs';
import * as path from 'node:path';
import { resolveIsolatedSessionParentPathFromCheckouts } from '../../paths/session-layout-paths.js';
import type { SessionWorktreeBinding } from '../session-worktree-binding.js';
import { collectWorktreeRootsNamed } from './collect-worktree-paths.js';
import { discoverLegacyBindingAscendingFromCheckout } from './discover-legacy-binding-ascending-from-checkout.js';
import { isGitDir } from './disk-walk-utils.js';
import { tryBindingFromSessionDirectory } from './try-binding-from-session-directory.js';

/**
 * When session meta has a path to the session directory, the bridge-key layout root, or a parent directory,
 * discover per-repo checkouts for `sessionId` (new or legacy layout).
 */
export async function discoverSessionWorktreesUnderSessionWorktreeRoot(
  sessionParentPathOrHint: string,
  sessionId: string,
): Promise<SessionWorktreeBinding | null> {
  const sid = sessionId.trim();
  if (!sid) return null;
  const hint = path.resolve(sessionParentPathOrHint);

  const underHint = await tryBindingFromSessionDirectory(path.join(hint, sid));
  if (underHint) return underHint;

  const direct = await tryBindingFromSessionDirectory(hint);
  if (direct) {
    if (path.basename(hint) === sid && (await isGitDir(hint))) {
      const legacyFromCheckout = await discoverLegacyBindingAscendingFromCheckout(sid, hint);
      if (legacyFromCheckout && legacyFromCheckout.repoCheckoutPaths.length > direct.repoCheckoutPaths.length) {
        return legacyFromCheckout;
      }
    }
    return direct;
  }

  if (path.basename(hint) === sid && (await isGitDir(hint))) {
    const legacyFromCheckout = await discoverLegacyBindingAscendingFromCheckout(sid, hint);
    if (legacyFromCheckout) return legacyFromCheckout;
  }

  let st: fs.Stats;
  try {
    st = await fs.promises.stat(hint);
  } catch {
    return null;
  }
  if (!st.isDirectory()) return null;

  const legacyPaths = await collectWorktreeRootsNamed(hint, sid);
  if (legacyPaths.paths.length === 0) return null;
  const isolated =
    resolveIsolatedSessionParentPathFromCheckouts(legacyPaths.paths) ?? path.resolve(legacyPaths.paths[0]!);
  return {
    sessionParentPath: path.resolve(isolated),
    repoCheckoutPaths: legacyPaths.paths.map((p) => path.resolve(p)),
  };
}
